import React, { useState } from 'react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const planillas = [{
  number: '01',
  icon: '📋',
  title: 'Historia Clínica Completa',
  price: '$9.000',
  description: 'El registro más importante de tu clínica, ahora sin errores ni datos faltantes.',
  features: ['Datos del propietario y del paciente', 'Anamnesis, examen físico por sistemas', 'Diagnóstico diferencial y plan terapéutico', 'Seguimiento de consultas anteriores', 'Espacio para evolución y observaciones']
}, {
  number: '02',
  icon: '💉',
  title: 'Control de Vacunación',
  price: '$7.000',
  description: 'Nunca más un paciente con vacunas vencidas sin que vos lo sepas.',
  features: ['Calendario de vacunas por paciente', 'Alertas de próximos vencimientos', 'Registro de lote y laboratorio', 'Historial completo por animal', 'Vista mensual de vacunaciones pendientes']
}, {
  number: '03',
  icon: '🐾',
  title: 'Seguimiento de Pacientes',
  price: '$6.000',
  description: 'Todos tus pacientes activos bajo control en una sola pantalla.',
  features: ['Base de datos de pacientes con ficha rápida', 'Estado actual: en tratamiento, alta, control', 'Próxima consulta programada', 'Historial de visitas resumido', 'Filtros por especie, raza y veterinario']
}, {
  number: '04',
  icon: '📅',
  title: 'Gestión de Turnos',
  price: '$6.000',
  description: 'La agenda de tu clínica organizada sin solapamientos ni olvidos.',
  features: ['Agenda diaria y semanal por profesional', 'Registro de asistencia y ausencias', 'Tipo de consulta: control, urgencia, cirugía', 'Datos de contacto del propietario integrados', 'Tiempo estimado por tipo de consulta']
}, {
  number: '05',
  icon: '💊',
  title: 'Inventario de Medicamentos',
  price: '$6.000',
  description: 'Nunca más quedarte sin un medicamento clave en medio de una consulta.',
  features: ['Stock actualizado por producto', 'Fecha de vencimiento y alertas', 'Registro de entradas y salidas', 'Proveedor y precio por unidad', 'Alerta de stock mínimo automática']
}, {
  number: '06',
  icon: '📝',
  title: 'Formularios Editables',
  price: '$5.000',
  description: 'Todos los formularios que tu clínica necesita, listos para personalizar.',
  features: ['Consentimiento informado para cirugías', 'Autorización de internación', 'Presupuesto de tratamiento', 'Alta médica con indicaciones', 'Remito de entrega de animal']
}];
const includes = ['Lista para usar — sin configuración compleja', 'Compatible con Excel y Google Sheets', 'Editable: agregá tu logo y datos de clínica', 'Descarga inmediata tras el pago', 'Actualización gratuita por 12 meses'];
export function Planillas() {
  const [openIndex, setOpenIndex] = useState(0);
  return <section id="planillas" className="bg-navy text-white py-12 px-6" data-id="element-168">
      <div className="max-w-md mx-auto space-y-8" data-id="element-169">
        <div className="text-center space-y-3" data-id="element-170">
          <div className="inline-block bg-gold text-navy px-4 py-1 rounded-full text-sm font-bold" data-id="element-171">
            📦 Kit Completo
          </div>
          <h2 className="text-2xl font-bold" data-id="element-172">6 tomos. Todo resuelto.</h2>
          <p className="text-[15px] opacity-90" data-id="element-173">
            De la consulta al cierre del día, sin nada sin registrar.
          </p>
          <p className="text-lg font-bold text-gold" data-id="element-174">
            Hoy por tan solo $19.990
          </p>
        </div>

        {/* Info card — sistema resumen */}
        <div className="bg-teal rounded-2xl p-6 text-white shadow-xl" data-id="element-175">
          <div className="flex justify-center" data-id="element-176">
            <div className="inline-flex items-center gap-2 border border-white/40 rounded-full px-4 py-1.5 text-sm font-semibold" data-id="element-177">
              <CheckIcon className="w-4 h-4" data-id="element-178" />
              SISTEMA N°1 EN ARGENTINA
            </div>
          </div>

          <div className="grid grid-cols-2 divide-x divide-white/20 mt-6 mb-5" data-id="element-179">
            <div className="text-center px-4" data-id="element-180">
              <div className="text-4xl font-extrabold" data-id="element-181">6</div>
              <div className="text-xs tracking-wide opacity-80 mt-1" data-id="element-182">TOMOS</div>
            </div>
            <div className="text-center px-4" data-id="element-183">
              <div className="text-4xl font-extrabold" data-id="element-184">4</div>
              <div className="text-xs tracking-wide opacity-80 mt-1" data-id="element-185">
                FORMATOS
              </div>
            </div>
          </div>

          <p className="text-center text-sm font-medium leading-relaxed" data-id="element-186">
            Evaluación · Diagnóstico · Seguimiento · Informes · Gestión
          </p>

          <div className="flex flex-wrap justify-center gap-2 mt-5" data-id="element-187">
            {['Excel', 'Google Sheets', 'Word', 'PDF', 'Editables', 'Imprimibles'].map(tag => <span key={tag} className="bg-teal-bright/25 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold" data-id="element-188">
                {tag}
              </span>)}
          </div>
        </div>

        <div className="space-y-3" data-id="element-189">
          {planillas.map((planilla, index) => <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden" data-id="element-190">
              <button onClick={() => setOpenIndex(openIndex === index ? -1 : index)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors" data-id="element-191">
                <div className="flex items-center gap-3" data-id="element-192">
                  <span className="text-gold font-bold text-lg" data-id="element-193">
                    {planilla.number}
                  </span>
                  <span className="text-2xl" data-id="element-194">{planilla.icon}</span>
                  <div data-id="element-195">
                    <span className="font-bold block" data-id="element-196">{planilla.title}</span>
                    <span className="text-xs text-teal-bright font-semibold" data-id="element-197">
                      Valor {planilla.price}
                    </span>
                  </div>
                </div>
                <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-198" />
              </button>

              <AnimatePresence data-id="element-199">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-200">
                    <div className="px-4 pb-4 space-y-4" data-id="element-201">
                      <p className="text-sm opacity-90 italic" data-id="element-202">
                        "{planilla.description}"
                      </p>
                      <div className="space-y-2" data-id="element-203">
                        {planilla.features.map((feature, idx) => <div key={idx} className="flex items-start gap-2" data-id="element-204">
                            <CheckIcon className="w-4 h-4 text-teal-bright flex-shrink-0 mt-0.5" data-id="element-205" />
                            <span className="text-sm opacity-90" data-id="element-206">
                              {feature}
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-teal-bright/30 rounded-xl p-6 space-y-3" data-id="element-207">
          <p className="font-bold text-teal-bright" data-id="element-208">Cada planilla incluye:</p>
          <div className="space-y-2" data-id="element-209">
            {includes.map((item, index) => <div key={index} className="flex items-start gap-2" data-id="element-210">
                <CheckIcon className="w-4 h-4 text-teal-bright flex-shrink-0 mt-0.5" data-id="element-211" />
                <span className="text-sm opacity-90" data-id="element-212">{item}</span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}